import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, MapPin, Loader2, Calendar, Package } from "lucide-react";

/**
 * AdminSourcingROI — per-vendor profitability leaderboard.
 *
 * Nifty knows what she paid in *marketplace fees* but nothing about *where she
 * bought the piece*. That sourcing side is entirely inside Maison Pilet and
 * is the sharpest lens on which physical hunting grounds actually pay for
 * themselves. This page rolls the whole inventory up by `source_vendor` and
 * ranks by net-profit so a new sourcing trip decision takes 30 seconds
 * instead of a spreadsheet.
 */
export default function AdminSourcingROI() {
  const [rows, setRows] = useState(null);   // null = loading | Array
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/sourcing-roi");
        setRows(data.vendors || []);
        setTotal(data.total_vendors || 0);
      } catch (e) {
        toast.error(`could not load sourcing report: ${e.response?.data?.detail || e.message}`);
        setRows([]);
      }
    })();
  }, []);

  const totals = rows?.reduce((acc, r) => ({
    spent: acc.spent + (r.total_spent || 0),
    revenue: acc.revenue + (r.total_revenue || 0),
    net: acc.net + (r.net_profit || 0),
    sourced: acc.sourced + (r.units_sourced || 0),
    sold: acc.sold + (r.units_sold || 0),
  }), { spent: 0, revenue: 0, net: 0, sourced: 0, sold: 0 }) || null;

  return (
    <AdminLayout>
      <div className="p-6 sm:p-10 space-y-6 max-w-7xl">
        <header>
          <p className="editorial-label mb-1">analytics · sourcing intelligence</p>
          <h1 className="font-serif text-4xl text-charcoal">sourcing ROI</h1>
          <p className="text-espresso mt-2 max-w-3xl text-sm">
            which flea markets, thrift stores and estate sales actually pay for themselves.
            ranked by net profit after marketplace fees and shipping label costs.
            data nifty can't see — this is your real moat.
          </p>
        </header>

        {rows === null && (
          <div className="flex items-center gap-2 text-taupe text-sm" data-testid="sourcing-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> loading…
          </div>
        )}

        {rows && rows.length === 0 && (
          <div className="border border-taupe/40 p-8 text-center text-taupe text-sm" data-testid="sourcing-empty">
            no sourcing data yet. add <em>source vendor</em> on your inventory items and the leaderboard fills in as pieces sell.
          </div>
        )}

        {rows && rows.length > 0 && totals && (
          <>
            {/* Totals strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="sourcing-totals">
              <StatCard label="net profit" value={fmtUSD(totals.net)} tone={totals.net >= 0 ? "positive" : "negative"} />
              <StatCard label="total spent" value={fmtUSD(totals.spent)} tone="neutral" />
              <StatCard label="total revenue" value={fmtUSD(totals.revenue)} tone="neutral" />
              <StatCard label="pieces sourced" value={`${totals.sourced} → ${totals.sold} sold`} tone="neutral" />
            </div>

            {/* Vendor leaderboard */}
            <div className="border border-taupe/40 bg-ivory overflow-x-auto">
              <table className="w-full text-xs" data-testid="sourcing-table">
                <thead className="bg-linen border-b border-taupe/40">
                  <tr className="text-[0.6rem] tracking-widest uppercase text-taupe">
                    <th className="px-4 py-3 text-left">vendor</th>
                    <th className="px-3 py-3 text-right">sourced</th>
                    <th className="px-3 py-3 text-right">sold</th>
                    <th className="px-3 py-3 text-right">sell-through</th>
                    <th className="px-3 py-3 text-right">spent</th>
                    <th className="px-3 py-3 text-right">revenue</th>
                    <th className="px-3 py-3 text-right">fees</th>
                    <th className="px-3 py-3 text-right">net</th>
                    <th className="px-3 py-3 text-right">margin</th>
                    <th className="px-3 py-3 text-right">avg days</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={r.vendor}
                      className={`border-b border-taupe/20 ${i === 0 && r.net_profit > 0 ? "bg-emerald-50/30" : ""}`}
                      data-testid={`sourcing-row-${i}`}
                    >
                      <td className="px-4 py-2.5 text-charcoal font-medium flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-brass flex-shrink-0" />
                        {r.vendor}
                      </td>
                      <td className="px-3 py-2.5 text-right">{r.units_sourced}</td>
                      <td className="px-3 py-2.5 text-right">{r.units_sold}</td>
                      <td className="px-3 py-2.5 text-right">{r.sell_through_pct}%</td>
                      <td className="px-3 py-2.5 text-right text-taupe">{fmtUSD(r.total_spent)}</td>
                      <td className="px-3 py-2.5 text-right text-taupe">{fmtUSD(r.total_revenue)}</td>
                      <td className="px-3 py-2.5 text-right text-taupe">{fmtUSD(r.total_fees)}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${r.net_profit >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                        {r.net_profit >= 0 ? <TrendingUp className="inline w-3 h-3 mr-1" /> : <TrendingDown className="inline w-3 h-3 mr-1" />}
                        {fmtUSD(r.net_profit)}
                      </td>
                      <td className={`px-3 py-2.5 text-right ${r.avg_margin_pct == null ? "text-taupe" : r.avg_margin_pct >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                        {r.avg_margin_pct == null ? "—" : `${r.avg_margin_pct}%`}
                      </td>
                      <td className="px-3 py-2.5 text-right text-taupe">
                        {r.avg_days_to_sell == null ? "—" : `${r.avg_days_to_sell}d`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[0.6rem] tracking-widest uppercase text-taupe">
              {total} vendor{total === 1 ? "" : "s"} tracked · sorted by net profit
            </p>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ label, value, tone }) {
  const cls = {
    positive: "border-emerald-500/60 bg-emerald-50 text-emerald-800",
    negative: "border-red-500/50 bg-red-50 text-red-800",
    neutral: "border-taupe/50 bg-ivory text-charcoal",
  }[tone];
  return (
    <div className={`border p-4 ${cls}`}>
      <p className="text-2xl font-serif">{value}</p>
      <p className="text-[0.6rem] tracking-widest uppercase mt-1 text-taupe">{label}</p>
    </div>
  );
}
